import React from "react";
import { Pagination, Spin, Tooltip } from "antd";
import styles from "./BenchmarkVersionHistory.module.scss";

/** Naive server timestamp (no timezone) → "Aug 7, 2026 · 14:23". Shown as-is. */
const formatTimestamp = (ts) => {
  if (!ts) return "";
  const m = String(ts).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return String(ts);
  const [, y, mo, d, hh, mm] = m;
  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${MONTHS[Number(mo) - 1]} ${Number(d)}, ${y} · ${hh}:${mm}`;
};

const BenchmarkVersionHistory = ({
  versions = [],
  total = 0,
  page = 0,
  limit = 20,
  loading = false,
  currentVersion = null,
  previewVersion = null,
  reverting = false,
  onPageChange,
  onPreview,
  onRevert,
}) => {
  return (
    <div className={styles.wrap}>
      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : !versions.length ? (
        <div className={styles.empty}>
          No saved versions yet. Save your current view to start a history.
        </div>
      ) : (
        <ul className={styles.list}>
          {versions.map((v) => {
            const isCurrent = v.is_current || v.version === currentVersion;
            const isPreview = v.version === previewVersion;
            return (
              <li
                key={v.version}
                className={`${styles.row} ${isCurrent ? styles["row--current"] : ""}`}
              >
                <div className={styles.meta}>
                  <div className={styles.titleRow}>
                    <span className={styles.version}>v{v.version}</span>
                    {isCurrent ? (
                      <span className={styles.badge}>Current</span>
                    ) : null}
                    {isPreview && !isCurrent ? (
                      <span className={styles["badge--preview"]}>
                        Previewing
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.label}>
                    {v.label || <em className={styles.noLabel}>No label</em>}
                  </div>
                  <div className={styles.time}>
                    {formatTimestamp(v.created_at)}
                  </div>
                </div>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => onPreview?.(v.version)}
                    disabled={reverting}
                  >
                    Preview
                  </button>
                  <Tooltip
                    title={
                      isCurrent
                        ? "This is already the current version"
                        : `Create a new version copying v${v.version}`
                    }
                  >
                    <span>
                      <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() => onRevert?.(v.version)}
                        disabled={isCurrent || reverting}
                      >
                        Revert
                      </button>
                    </span>
                  </Tooltip>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {total > limit ? (
        <div className={styles.pager}>
          <Pagination
            simple
            size="small"
            current={page + 1}
            pageSize={limit}
            total={total}
            onChange={(p) => onPageChange?.(p - 1)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default BenchmarkVersionHistory;
